import BaseStore from 'customer-flows/stores/base-store'
import { trackedAsync, type Awaitable } from 'customer-flows/utils/core'
import { tracked } from '@glimmer/tracking'

export default class QuoteStoreClass extends BaseStore {
  @trackedAsync() quote: Awaitable<Quote>

  // Maps paymentSource.id (e.g., "ps_abc12") to full redemption code (e.g., "VUT8UBIG")
  // Needed because backend only returns last 4 characters for stored value payments,
  // but removeRedemptionCode mutation requires the full code. Keyed by the unique
  // paymentSource id because two codes can share the same last 4 characters
  @tracked fullRedemptionCodeByPaymentSourceIdMap: Map<string, string> = new Map()
  @tracked cachedQuoteId?: string // if quote fails, we lose the ID

  get id(): string | undefined {
    return this.quote.value?.id ?? this.cachedQuoteId
  }

  get hasQuote(): boolean {
    return !!this.quote.value
  }

  get expiresAt(): string | undefined {
    return this.quote.value?.expiresAt
  }

  get priceBreakdown(): PriceBreakdown | undefined {
    return this.quote.value?.priceBreakdown
  }

  get lines(): QuoteLine[] {
    return (this.quote.value?.bookingQuotes ?? []).flatMap((bookingQuote) => bookingQuote.lines ?? [])
  }

  get addonLines(): QuoteLine[] {
    return this.lines.filter((line) => line.isChild)
  }

  get taxesAndFees(): Money | undefined {
    return this.quote.value?.priceBreakdown?.taxesAndFees
  }

  get peekProtectFee(): Money | undefined {
    const fee = this.quote.value?.priceBreakdown?.peekProtectFee
    if (!fee?.amount || parseFloat(fee.amount) <= 0) {
      return undefined
    }
    return fee
  }

  get tips(): Money | undefined {
    return this.quote.value?.priceBreakdown?.tips
  }

  get tippingAdded(): boolean {
    return parseFloat(this.tips?.amount ?? '0') > 0
  }

  get promoCodes(): QuotePromoCode[] | undefined {
    return this.quote.value?.promoCodes
  }

  get authorizedPayments(): QuotePayment[] | undefined {
    return this.quote.value?.authorizedPayments
  }

  get amountToPay(): number {
    const storedValueAuthorized = this.priceBreakdown?.storedValueAuthorized?.amount
    const hasStoredValuePayments = storedValueAuthorized && parseFloat(storedValueAuthorized) > 0

    const amount = hasStoredValuePayments
      ? Number(this.priceBreakdown?.totalMinusStoredValueAuthorized?.amount ?? 0)
      : Number(this.priceBreakdown?.total?.amount ?? 0)

    return Math.round(amount * 100)
  }

  get isFree(): boolean {
    return this.amountToPay === 0
  }

  get quoteExpirationTimeInSeconds(): number {
    if (!this.expiresAt) {
      // Default to 10 minutes when backend doesn't provide an expiration time
      return 600
    }

    const expiresAtTime = new Date(this.expiresAt).getTime()
    const currentTime = Date.now()
    const remainingMs = expiresAtTime - currentTime

    if (remainingMs <= 0) {
      return 0
    }

    return Math.floor(remainingMs / 1000)
  }

  get peekProtectSuggestion(): PeekProtectSuggestion | undefined {
    return this.quote.value?.peekProtectSuggestion
  }

  get showPeekProtectSuggestion(): boolean {
    const suggestionValue = this.peekProtectSuggestion?.value?.amount
    return !!suggestionValue && parseFloat(suggestionValue) !== 0
  }

  get peekProtectVisible(): boolean {
    return !this.isFree && this.showPeekProtectSuggestion
  }

  get hasPeekProtect(): boolean {
    const peekProtectFee = this.priceBreakdown?.peekProtectFee?.amount
    return !!peekProtectFee && parseFloat(peekProtectFee) !== 0
  }

  get hasTaxesAndFees(): boolean {
    return this.taxesAndFees?.amount ? parseFloat(this.taxesAndFees.amount) > 0 : false
  }

  get appliedPromoCodes(): QuotePromoCode[] {
    return (this.promoCodes || []).filter((promoCode: QuotePromoCode) => promoCode.isApplied)
  }

  get promoCodeDiscount(): Money | undefined {
    const priceBreakdown = this.priceBreakdown
    const discount = priceBreakdown?.discount

    if (this.appliedPromoCodes.length === 0 || !discount?.amount) {
      return undefined
    }

    const discountAmount = parseFloat(discount.amount)
    if (discountAmount <= 0) {
      return undefined
    }

    return discount
  }

  get cardIssuerDiscountRedeemable(): boolean {
    return !!this.quote.value?.cardIssuerDiscountRedeemable
  }

  get cardIssuerDiscount(): Money | undefined {
    const discount = this.priceBreakdown?.cardIssuerDiscount
    if (!discount?.amount || parseFloat(discount.amount) <= 0) {
      return undefined
    }
    return discount
  }

  get cardIssuerDiscountApplied(): boolean {
    return !!this.cardIssuerDiscount
  }

  get appliedGiftCards(): StoredValuePayment[] {
    return ((this.authorizedPayments || []) as StoredValuePayment[]).filter(
      (payment: StoredValuePayment) => payment.paymentSource?.category === 'gift_card'
    )
  }

  get appliedStoreCredits(): StoredValuePayment[] {
    return ((this.authorizedPayments || []) as StoredValuePayment[]).filter(
      (payment: StoredValuePayment) => payment.paymentSource?.category === 'store_credit'
    )
  }

  get appliedExternalVouchers(): StoredValuePayment[] {
    return ((this.authorizedPayments || []) as StoredValuePayment[]).filter(
      (payment: StoredValuePayment) => payment.paymentSource?.category === 'external_voucher'
    )
  }

  get hasAppliedStoredValuePayments(): boolean {
    return (
      this.appliedGiftCards.length > 0 || this.appliedStoreCredits.length > 0 || this.appliedExternalVouchers.length > 0
    )
  }

  get orderTotal(): Money | undefined {
    const priceBreakdown = this.priceBreakdown

    return this.hasAppliedStoredValuePayments ? priceBreakdown?.totalMinusStoredValueAuthorized : priceBreakdown?.total
  }

  isPromoCodeApplied(code: string): boolean {
    return this.appliedPromoCodes.some((promoCode: QuotePromoCode) => promoCode.code === code)
  }
}

declare global {
  export type QuoteStore = QuoteStoreClass
  export interface StoreRegistry {
    quote: QuoteStore
  }
}
